import type { PageHelp } from '../../help/types';

/**
 * Guide for `/` — the dashboard, plus the application frame (top bar and navigation) that is on
 * every screen and is explained once, here.
 */
export const dashboardHelp: PageHelp = {
  title: 'Dashboard',
  intro:
    'Where every project you work on stands: how much of each locale is translated, and what is waiting for somebody to act. The top bar and the menu on the left are on every screen and are explained here as well.',
  sections: ['Getting around', 'At a glance', 'Needs attention', 'Your projects'],
  tips: [
    'Coverage counts machine translations as translated. Whether they are also published depends on the project setting “Export machine translations”.',
    'Every screen with a “?” in the top bar has its own guide. The language you choose in one guide is kept for all of them.',
  ],
  steps: [
    // ── Getting around ──────────────────────────────────────────────────────────
    {
      selector: '[data-help="shell-nav-main"]',
      title: 'Move between the main screens',
      body: 'Dashboard, Projects, Packages and Glossary are always available. The highlighted entry is the screen you are on.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Zwischen den Hauptseiten wechseln',
          body: 'Dashboard, Projects, Packages und Glossary sind immer erreichbar. Der hervorgehobene Eintrag ist die Seite, auf der Sie sich gerade befinden.',
        },
        'sr-Latn-RS': {
          title: 'Prelazak između glavnih ekrana',
          body: 'Dashboard, Projects, Packages i Glossary su uvek dostupni. Istaknuta stavka je ekran na kojem se trenutno nalazite.',
        },
      },
    },
    {
      selector: '[data-help="shell-nav-project"]',
      title: 'Open a screen of the current project',
      body: 'Keys, Linked keys, Releases, Imports, Missing keys, Suggestions, AI jobs and Activity all work on one project — the one chosen in the project switcher. Until a project is chosen these links are greyed out; pick one in the switcher, or open a project from this page or from the Projects list. AI jobs stays greyed out until AI translation is available. An orange number beside Releases, Missing keys or Suggestions counts what waits there for this project — the same items as the bell.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Eine Seite des aktuellen Projekts öffnen',
          body: 'Keys, Linked keys, Releases, Imports, Missing keys, Suggestions, AI jobs und Activity arbeiten alle mit einem Projekt — dem, das in der Projektauswahl gewählt ist. Solange kein Projekt gewählt ist, sind diese Links ausgegraut; wählen Sie eines in der Projektauswahl oder öffnen Sie ein Projekt auf dieser Seite oder in der Liste „Projects“. AI jobs bleibt ausgegraut, bis die KI-Übersetzung verfügbar ist. Eine orange Zahl neben Releases, Missing keys oder Suggestions zählt, was dort für dieses Projekt wartet — dieselben Einträge wie bei der Glocke.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje ekrana trenutnog projekta',
          body: 'Keys, Linked keys, Releases, Imports, Missing keys, Suggestions, AI jobs i Activity rade nad jednim projektom — onim koji je izabran u biraču projekta. Dok projekat nije izabran, ovi linkovi su sivi; izaberite ga u biraču ili otvorite projekat sa ove stranice ili sa liste „Projects“. AI jobs ostaje siv dok AI prevod ne postane dostupan. Narandžasti broj pored Releases, Missing keys ili Suggestions broji ono što tamo čeka za ovaj projekat — iste stavke kao u zvoncetu.',
        },
      },
    },
    {
      selector: '[data-help="shell-nav-admin"]',
      title: 'Reach the administration screens',
      body: 'Users, API tokens, Locales and Publish targets are shown only to administrators. Users decides who may sign in and what each person may do, API tokens are credentials for pipelines, Locales lists every language Glossa knows, and Publish targets shows which project or package is published to which environment.',
      section: 'Getting around',
      minRole: 'admin',
      i18n: {
        'de-AT': {
          title: 'Die Administrationsseiten erreichen',
          body: 'Users, API tokens, Locales und Publish targets sehen nur Administratoren. Unter Users wird festgelegt, wer sich anmelden darf und was jede Person darf, API tokens sind Zugangsdaten für Pipelines, Locales listet alle Sprachen, die Glossa kennt, und Publish targets zeigt, welches Projekt oder Paket in welche Umgebung veröffentlicht wird.',
        },
        'sr-Latn-RS': {
          title: 'Pristup administrativnim ekranima',
          body: 'Users, API tokens, Locales i Publish targets vide samo administratori. Users određuje ko sme da se prijavi i šta svaka osoba sme da radi, API tokens su pristupni podaci za pipeline-ove, Locales prikazuje sve jezike koje Glossa poznaje, a Publish targets pokazuje koji se projekat ili paket objavljuje u koje okruženje.',
        },
      },
    },
    {
      selector: '[data-help="shell-project-switcher"]',
      title: 'Switch the current project',
      body: 'Lists your projects; type a code or a name to find one. Choosing one opens its Keys screen and makes it the project the Project links in the menu lead to. The switcher is hidden when you have no project at all.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Das aktuelle Projekt wechseln',
          body: 'Zeigt Ihre Projekte; tippen Sie einen Code oder Namen, um eines zu finden. Wenn Sie eines wählen, öffnet sich dessen Seite „Keys“, und die Projekt-Links im Menü führen ab dann zu diesem Projekt. Haben Sie gar kein Projekt, wird die Auswahl nicht angezeigt.',
        },
        'sr-Latn-RS': {
          title: 'Promena trenutnog projekta',
          body: 'Prikazuje vaše projekte; kucajte kod ili naziv da pronađete projekat. Kada izaberete jedan, otvara se njegov ekran „Keys“, a linkovi iz grupe Project u meniju od tada vode na taj projekat. Ako nemate nijedan projekat, birač se ne prikazuje.',
        },
      },
    },
    {
      selector: '[data-help="shell-search"]',
      title: 'Find a key anywhere',
      body: 'Searches every project you can read and every package, from any screen; Ctrl+K puts the cursor here. Type at least two characters of a key name, a description or a text as the application shows it — values are searched in each project’s or package’s source locale. The best eight hits appear as you pause; Enter or a click opens the key in its own keys list, and the last row opens all results on the Key search page.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Einen Key überall finden',
          body: 'Durchsucht jedes Projekt, das Sie lesen dürfen, und jedes Paket — von jeder Seite aus; Strg+K setzt den Cursor hierher. Geben Sie mindestens zwei Zeichen eines Key-Namens, einer Beschreibung oder eines Textes ein, wie ihn die Anwendung zeigt — Werte werden in der Quellsprache des jeweiligen Projekts oder Pakets durchsucht. Sobald Sie pausieren, erscheinen die acht besten Treffer; die Eingabetaste oder ein Klick öffnet den Key in seiner Key-Liste, die letzte Zeile öffnet alle Ergebnisse auf der Seite „Key search“.',
        },
        'sr-Latn-RS': {
          title: 'Pronalaženje ključa bilo gde',
          body: 'Pretražuje svaki projekat koji smete da čitate i svaki paket, sa bilo kog ekrana; Ctrl+K postavlja kursor ovde. Unesite bar dva znaka imena ključa, opisa ili teksta kakav aplikacija prikazuje — vrednosti se pretražuju na izvornom jeziku svakog projekta ili paketa. Kada zastanete, pojavljuje se osam najboljih pogodaka; Enter ili klik otvara ključ u njegovoj listi ključeva, a poslednji red otvara sve rezultate na stranici „Key search“.',
        },
      },
    },
    {
      selector: '[data-help="shell-notifications"]',
      title: 'See what is waiting for you',
      body: 'The bell lists three kinds of work: a failed release that no later release of the same target has replaced, missing keys a running application reported, and package suggestions nobody has decided yet. There is one entry per kind and project or package, and the badge counts the ones you have not read yet. Click an entry to open the screen where the work is done, or use the tick (or Mark all as read) to quiet the badge without leaving the page. A read entry stays in the list, dimmed, until the work is done, and turns new again when its number grows — another failed release, another reported key. Read marks are kept in this browser.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Sehen, was auf Sie wartet',
          body: 'Die Glocke listet drei Arten von Arbeit: ein fehlgeschlagenes Release, das noch kein späteres Release desselben Ziels ersetzt hat, fehlende Keys, die eine laufende Anwendung gemeldet hat, und Paketvorschläge, über die noch niemand entschieden hat. Es gibt einen Eintrag pro Art und Projekt bzw. Paket, und die Zahl am Symbol zählt die, die Sie noch nicht gelesen haben. Ein Klick auf einen Eintrag öffnet die Seite, auf der die Arbeit erledigt wird; mit dem Häkchen (oder „Mark all as read“) beruhigen Sie die Zahl, ohne die Seite zu verlassen. Ein gelesener Eintrag bleibt abgeblendet in der Liste, bis die Arbeit erledigt ist, und wird wieder neu, sobald seine Zahl wächst — ein weiteres fehlgeschlagenes Release, ein weiterer gemeldeter Key. Die Lesemarken werden in diesem Browser gespeichert.',
        },
        'sr-Latn-RS': {
          title: 'Pregled onoga što vas čeka',
          body: 'Zvonce prikazuje tri vrste posla: neuspelo izdanje koje nijedno kasnije izdanje istog cilja nije zamenilo, nedostajuće ključeve koje je prijavila pokrenuta aplikacija i predloge za pakete o kojima još niko nije odlučio. Postoji jedna stavka po vrsti i projektu ili paketu, a broj na ikonici broji one koje još niste pročitali. Kliknite na stavku da otvorite ekran na kojem se posao radi, ili kvačicom (ili sa „Mark all as read“) utišajte broj bez napuštanja stranice. Pročitana stavka ostaje u listi, posivljena, dok posao ne bude završen, i ponovo postaje nova kada joj broj poraste — još jedno neuspelo izdanje, još jedan prijavljen ključ. Oznake pročitanog čuvaju se u ovom pregledaču.',
        },
      },
    },
    {
      selector: '[data-help="shell-help"]',
      title: 'Open the guide for this screen',
      body: 'Opens the page guide for the screen you are on, with a search box and a language switch (EN, DE, SR). “Show me around” walks over the controls that are on screen, and “Point at a field” explains the control you click. Screens without a guide do not show the “?”.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Die Hilfe zu dieser Seite öffnen',
          body: 'Öffnet die Seitenhilfe zur aktuellen Seite, mit Suchfeld und Sprachumschaltung (EN, DE, SR). „Tour starten“ führt über die Elemente, die gerade sichtbar sind, und „Auf ein Feld zeigen“ erklärt das Element, das Sie anklicken. Seiten ohne Hilfe zeigen kein „?“.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje vodiča za ovaj ekran',
          body: 'Otvara vodič za ekran na kojem se nalazite, sa poljem za pretragu i izborom jezika (EN, DE, SR). „Provedi me“ vas vodi kroz kontrole koje su trenutno vidljive, a „Pokaži na polje“ objašnjava kontrolu na koju kliknete. Ekrani bez vodiča ne prikazuju „?“.',
        },
      },
    },
    {
      selector: '[data-help="shell-user-menu"]',
      title: 'Check your account, switch light or dark, or sign out',
      body: 'Your name, at the foot of the menu on the left, opens a menu with your e-mail address, your global roles, “Appearance” and “Sign out”. Appearance switches between Light and Dark; System, the default, follows your computer’s setting, and the choice is kept in this browser. Roles you hold on a single project are not listed there; the Projects screen shows those.',
      section: 'Getting around',
      i18n: {
        'de-AT': {
          title: 'Konto prüfen, hell oder dunkel wählen, abmelden',
          body: 'Ihr Name unten im Menü links öffnet ein Menü mit Ihrer E-Mail-Adresse, Ihren globalen Rollen, „Appearance“ und „Sign out“. Unter „Appearance“ wählen Sie „Light“ oder „Dark“; „System“, die Voreinstellung, folgt der Einstellung Ihres Computers. Die Wahl wird in diesem Browser gespeichert. Rollen, die Sie nur in einem einzelnen Projekt haben, stehen dort nicht; diese zeigt die Seite „Projects“.',
        },
        'sr-Latn-RS': {
          title: 'Provera naloga, svetla ili tamna tema, odjava',
          body: 'Vaše ime, na dnu menija levo, otvara meni sa vašom e-mail adresom, globalnim ulogama, opcijom „Appearance“ i opcijom „Sign out“. „Appearance“ menja izgled između „Light“ i „Dark“; „System“, podrazumevani izbor, prati podešavanje vašeg računara, a izbor se pamti u ovom pregledaču. Uloge koje imate samo na pojedinačnom projektu tu nisu navedene; njih prikazuje ekran „Projects“.',
        },
      },
    },

    // ── At a glance ─────────────────────────────────────────────────────────────
    {
      selector: '[data-help="dashboard-summary"]',
      title: 'Read the headline numbers',
      body: 'The projects you can reach, their keys, how much of every target locale is written, and how many items need attention. Translated leaves each project’s source locale out, so it answers how much is left to translate. Keys counts a package or linked key once in every project that exports it. Clicking Needs attention jumps to the list below.',
      section: 'At a glance',
      i18n: {
        'de-AT': {
          title: 'Die Kennzahlen lesen',
          body: 'Die Projekte, auf die Sie Zugriff haben, ihre Keys, wie viel jeder Zielsprache geschrieben ist und wie viele Einträge Handlungsbedarf haben. „Translated“ lässt die Ausgangssprache jedes Projekts weg und sagt deshalb, wie viel noch zu übersetzen ist. „Keys“ zählt einen Paket- oder verknüpften Key in jedem Projekt, das ihn exportiert. Ein Klick auf „Needs attention“ springt zur Liste darunter.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje ključnih brojeva',
          body: 'Projekti kojima imate pristup, njihovi ključevi, koliko je svakog ciljnog jezika napisano i koliko stavki zahteva pažnju. „Translated“ izostavlja izvorni jezik svakog projekta, pa pokazuje koliko je još ostalo da se prevede. „Keys“ broji ključ iz paketa ili povezani ključ u svakom projektu koji ga izvozi. Klik na „Needs attention“ vodi na listu ispod.',
        },
      },
    },

    // ── Needs attention ─────────────────────────────────────────────────────────
    {
      selector: '[data-help="dashboard-attention"]',
      title: 'Deal with blocked work first',
      body: 'Shown only when something is waiting: a failed release that nothing has been published over yet, missing keys a running build reported, or open suggestions on a package. It is the same list as the bell, placed above the coverage; a red error icon marks a failed release, an amber warning icon everything else.',
      section: 'Needs attention',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Blockierte Arbeit zuerst erledigen',
          body: 'Wird nur angezeigt, wenn etwas wartet: ein fehlgeschlagenes Release, über das noch nichts veröffentlicht wurde, fehlende Keys, die ein laufender Build gemeldet hat, oder offene Vorschläge zu einem Paket. Es ist dieselbe Liste wie bei der Glocke, hier über der Abdeckung; ein rotes Fehlersymbol kennzeichnet ein fehlgeschlagenes Release, ein gelbes Warnsymbol alles andere.',
        },
        'sr-Latn-RS': {
          title: 'Prvo rešite blokirani posao',
          body: 'Prikazuje se samo kada nešto čeka: neuspelo izdanje preko kojeg još ništa nije objavljeno, nedostajući ključevi koje je prijavio pokrenuti build ili otvoreni predlozi za paket. To je ista lista kao kod zvonceta, postavljena iznad pokrivenosti; crvena ikonica greške označava neuspelo izdanje, žuta ikonica upozorenja sve ostalo.',
        },
      },
    },
    {
      selector: '[data-help="dashboard-attention-open"]',
      title: 'Open the screen that resolves it',
      body: '“Open” takes you to where the item is handled: Releases for a failed publish, Missing keys for reported keys, Suggestions for a package proposal. The entry goes away once the work there is done — for example when a later publish of that target succeeds, or the reported keys are created or ignored.',
      section: 'Needs attention',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Seite öffnen, auf der es erledigt wird',
          body: '„Open“ führt dorthin, wo der Eintrag bearbeitet wird: Releases bei einem fehlgeschlagenen Publish, Missing keys bei gemeldeten Keys, Suggestions bei einem Paketvorschlag. Der Eintrag verschwindet, sobald die Arbeit dort erledigt ist — etwa wenn ein späterer Publish dieses Ziels gelingt oder die gemeldeten Keys angelegt oder ignoriert wurden.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje ekrana na kojem se to rešava',
          body: '„Open“ vas vodi tamo gde se stavka rešava: na Releases za neuspelo objavljivanje, na Missing keys za prijavljene ključeve, na Suggestions za predlog za paket. Stavka nestaje kada je posao tamo završen — na primer kada kasnije objavljivanje tog cilja uspe ili kada su prijavljeni ključevi kreirani ili ignorisani.',
        },
      },
    },

    // ── Your projects ───────────────────────────────────────────────────────────
    {
      selector: '[data-help="dashboard-projects"]',
      title: 'See where each project stands',
      body: 'One row per project you can reach and one column per locale any of them uses, the most widely used locales first. The project name and the Translated column stay in place while the locales scroll sideways. If you have no role on any project the page says so instead — an administrator grants access per project.',
      section: 'Your projects',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Den Stand jedes Projekts sehen',
          body: 'Eine Zeile pro Projekt, auf das Sie Zugriff haben, und eine Spalte pro Sprache, die eines davon verwendet, die meistverwendeten Sprachen zuerst. Projektname und die Spalte „Translated“ bleiben stehen, während die Sprachen seitlich scrollen. Haben Sie in keinem Projekt eine Rolle, steht stattdessen ein Hinweis da — den Zugriff vergibt ein Administrator pro Projekt.',
        },
        'sr-Latn-RS': {
          title: 'Pregled stanja svakog projekta',
          body: 'Jedan red za svaki projekat kojem imate pristup i jedna kolona za svaki jezik koji neki od njih koristi, najkorišćeniji jezici prvi. Naziv projekta i kolona „Translated“ ostaju na mestu dok se jezici pomeraju u stranu. Ako nemate ulogu ni na jednom projektu, stranica to prikazuje umesto tabele — pristup dodeljuje administrator za svaki projekat posebno.',
        },
      },
    },
    {
      selector: '[data-help="dashboard-search"]',
      title: 'Find a project',
      body: 'Filters the rows as you type, by project code or name. Any part matches, regardless of upper or lower case.',
      section: 'Your projects',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Ein Projekt finden',
          body: 'Filtert die Zeilen während der Eingabe nach Projektcode oder -name. Jeder Teil passt, unabhängig von Groß- und Kleinschreibung.',
        },
        'sr-Latn-RS': {
          title: 'Pronalaženje projekta',
          body: 'Filtrira redove dok kucate, po kodu ili nazivu projekta. Poklapa se bilo koji deo, bez obzira na velika i mala slova.',
        },
      },
    },
    {
      selector: '[data-help="dashboard-sort"]',
      title: 'Put the most work on top',
      body: '“Least covered first”, the default, lists the projects with the most left to translate on top. “Name” sorts alphabetically and “Most keys first” by size. A project with nothing to translate yet always comes last.',
      section: 'Your projects',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die meiste Arbeit nach oben holen',
          body: '„Least covered first“, die Voreinstellung, zeigt die Projekte mit dem meisten Übersetzungsrückstand oben. „Name“ sortiert alphabetisch, „Most keys first“ nach Größe. Ein Projekt, in dem es noch nichts zu übersetzen gibt, steht immer am Ende.',
        },
        'sr-Latn-RS': {
          title: 'Najviše posla na vrh',
          body: '„Least covered first“, podrazumevani izbor, stavlja na vrh projekte kojima je ostalo najviše za prevod. „Name“ ređa po abecedi, a „Most keys first“ po veličini. Projekat u kojem još nema ništa za prevod uvek je poslednji.',
        },
      },
    },
    {
      selector: '[data-help="dashboard-only-gaps"]',
      title: 'Hide finished projects',
      body: 'Leaves out every project whose target locales are all complete, so only the ones with work left remain.',
      section: 'Your projects',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Fertige Projekte ausblenden',
          body: 'Blendet jedes Projekt aus, dessen Zielsprachen alle vollständig sind, sodass nur die mit offener Arbeit übrig bleiben.',
        },
        'sr-Latn-RS': {
          title: 'Sakrivanje završenih projekata',
          body: 'Izostavlja svaki projekat čiji su svi ciljni jezici kompletni, pa ostaju samo oni u kojima ima posla.',
        },
      },
    },
    {
      selector: '[data-help="dashboard-legend"]',
      title: 'Read the colour scale',
      body: 'The darker the cell, the more is written, in five steps from under 50 % to 100 %. A cell shows 100 % only when not a single value is missing, so 99.9 % still reads 99. The source locale is grey with a dashed border, because there it only says how many keys have a source text; a dot marks a locale the project does not use.',
      section: 'Your projects',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Farbskala lesen',
          body: 'Je dunkler die Zelle, desto mehr ist geschrieben, in fünf Stufen von unter 50 % bis 100 %. Eine Zelle zeigt 100 % nur, wenn kein einziger Wert fehlt; 99,9 % steht also noch als 99 da. Die Ausgangssprache ist grau mit gestricheltem Rand, weil sie dort nur angibt, wie viele Keys einen Quelltext haben; ein Punkt kennzeichnet eine Sprache, die das Projekt nicht verwendet.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje skale boja',
          body: 'Što je ćelija tamnija, to je više napisano, u pet koraka od ispod 50 % do 100 %. Ćelija pokazuje 100 % samo kada ne nedostaje nijedna vrednost, pa 99,9 % i dalje piše 99. Izvorni jezik je siv sa isprekidanim okvirom, jer tamo samo pokazuje koliko ključeva ima izvorni tekst; tačka označava jezik koji projekat ne koristi.',
        },
      },
    },
    {
      selector: '[data-help="dashboard-coverage"]',
      title: 'Read the coverage',
      body: 'Each cell is the share of that locale’s values already written — reviewed, draft and machine alike — and a plural key counts once per plural form of the locale. Translated is the same for all target locales of the project together. Point at a cell for the exact numbers; for the split by status, open the project’s settings.',
      section: 'Your projects',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Abdeckung lesen',
          body: 'Jede Zelle ist der Anteil der bereits geschriebenen Werte dieser Sprache — reviewed, draft und machine gleichermaßen —, und ein Plural-Key zählt einmal pro Pluralform der Sprache. „Translated“ ist dasselbe für alle Zielsprachen des Projekts zusammen. Zeigen Sie auf eine Zelle, um die genauen Zahlen zu sehen; die Aufteilung nach Status finden Sie in den Projekteinstellungen.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje pokrivenosti',
          body: 'Svaka ćelija je udeo već napisanih vrednosti tog jezika — reviewed, draft i machine podjednako — a ključ sa množinom se broji jednom za svaki oblik množine tog jezika. „Translated“ je isto to za sve ciljne jezike projekta zajedno. Zadržite pokazivač na ćeliji za tačne brojeve; podelu po statusu pogledajte u podešavanjima projekta.',
        },
      },
    },
    {
      selector: '[data-help="dashboard-coverage-cell"]',
      title: 'Go straight to what is missing',
      body: 'Click a locale cell to open the project’s Keys screen filtered to “Missing in” that locale — the list of what is left. “Package keys” is switched on there, because the percentage counts the keys of the packages the project uses as well. Click a Translated cell to open the Keys screen unfiltered. Either way the project becomes the current one in the switcher.',
      section: 'Your projects',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Direkt zu dem, was fehlt',
          body: 'Ein Klick auf eine Sprachzelle öffnet die Seite „Keys“ des Projekts, gefiltert auf „Missing in“ dieser Sprache — die Liste dessen, was noch fehlt. „Package keys“ ist dort eingeschaltet, weil der Prozentsatz auch die Schlüssel der Pakete zählt, die das Projekt verwendet. Ein Klick auf eine Zelle in „Translated“ öffnet die Seite „Keys“ ohne Filter. In beiden Fällen wird das Projekt zum aktuellen Projekt in der Projektauswahl.',
        },
        'sr-Latn-RS': {
          title: 'Pravo na ono što nedostaje',
          body: 'Klik na ćeliju jezika otvara ekran „Keys“ projekta filtriran na „Missing in“ za taj jezik — listu onoga što je ostalo. „Package keys“ je tamo uključeno, jer procenat broji i ključeve paketa koje projekat koristi. Klik na ćeliju u koloni „Translated“ otvara ekran „Keys“ bez filtera. U oba slučaja projekat postaje trenutni projekat u biraču.',
        },
      },
    },
    {
      selector: '[data-help="dashboard-project-link"]',
      title: 'Start translating a project',
      body: 'Click the project name to open its Keys screen. This also makes it the current project in the switcher, so the Project links in the menu lead to it.',
      section: 'Your projects',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Mit dem Übersetzen eines Projekts beginnen',
          body: 'Klicken Sie auf den Projektnamen, um die Seite „Keys“ des Projekts zu öffnen. Das Projekt wird dabei auch in der Projektauswahl zum aktuellen Projekt, sodass die Projekt-Links im Menü dorthin führen.',
        },
        'sr-Latn-RS': {
          title: 'Početak prevođenja projekta',
          body: 'Kliknite na naziv projekta da otvorite njegov ekran „Keys“. Projekat time postaje i trenutni projekat u biraču, pa linkovi iz grupe Project u meniju vode na njega.',
        },
      },
    },
    {
      selector: '[data-help="dashboard-project-roles"]',
      title: 'Check your roles on a project',
      body: 'After the key count come your grants on this project: owner, translator, developer or viewer. They decide what you may do there. An administrator without a grant sees none, but may still do everything.',
      section: 'Your projects',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Ihre Rollen im Projekt prüfen',
          body: 'Nach der Anzahl der Keys stehen Ihre Berechtigungen in diesem Projekt: owner, translator, developer oder viewer. Sie bestimmen, was Sie dort tun dürfen. Ein Administrator ohne eigene Berechtigung sieht keine, darf aber trotzdem alles.',
        },
        'sr-Latn-RS': {
          title: 'Provera vaših uloga na projektu',
          body: 'Posle broja ključeva stoje vaša ovlašćenja na ovom projektu: owner, translator, developer ili viewer. Ona određuju šta tamo smete da radite. Administrator bez posebnog ovlašćenja ih ne vidi, ali i dalje sme sve.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Dashboard',
      intro:
        'Wo jedes Ihrer Projekte steht: wie viel jeder Sprache übersetzt ist und was darauf wartet, dass jemand handelt. Die obere Leiste und das Menü links sind auf jeder Seite vorhanden und werden ebenfalls hier erklärt.',
      sections: {
        'Getting around': 'Orientierung',
        'At a glance': 'Auf einen Blick',
        'Needs attention': 'Handlungsbedarf',
        'Your projects': 'Ihre Projekte',
      },
      tips: [
        'Die Abdeckung zählt Maschinenübersetzungen als übersetzt. Ob sie auch veröffentlicht werden, hängt von der Projekteinstellung „Export machine translations“ ab.',
        'Jede Seite mit einem „?“ in der oberen Leiste hat ihre eigene Hilfe. Die Sprache, die Sie in einer Hilfe wählen, gilt für alle.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Dashboard',
      intro:
        'Gde se nalazi svaki projekat na kojem radite: koliko je svakog jezika prevedeno i šta čeka da neko reaguje. Gornja traka i meni sa leve strane postoje na svakom ekranu i ovde su takođe objašnjeni.',
      sections: {
        'Getting around': 'Snalaženje',
        'At a glance': 'Na prvi pogled',
        'Needs attention': 'Zahteva pažnju',
        'Your projects': 'Vaši projekti',
      },
      tips: [
        'Pokrivenost mašinske prevode računa kao prevedene. Da li se oni i objavljuju zavisi od podešavanja projekta „Export machine translations“.',
        'Svaki ekran sa „?“ u gornjoj traci ima svoj vodič. Jezik koji izaberete u jednom vodiču važi za sve.',
      ],
    },
  },
};
